import React, { useMemo, useState } from 'react';
import { PULSE_SOURCE_CONFIG } from './pulseSourceConfig';

const SOURCE_ORDER = ['serff', 'pr_news', 'linkedin_ads', 'meta_ads', 'google_ads'];

function shortDayLabel(dateStr) {
  try {
    const d = new Date(`${dateStr}T12:00:00`);
    return d.toLocaleDateString('en-US', { weekday: 'short' });
  } catch {
    return dateStr;
  }
}

function fullDayLabel(dateStr) {
  try {
    const d = new Date(`${dateStr}T12:00:00`);
    return d.toLocaleDateString('en-US', { weekday: 'long', month: 'short', day: 'numeric' });
  } catch {
    return dateStr;
  }
}

function weekLabel(dateStr) {
  try {
    const start = new Date(`${dateStr}T12:00:00`);
    const end = new Date(start);
    end.setDate(end.getDate() + 6);
    const a = start.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
    const b = end.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
    return `${a} – ${b}`;
  } catch {
    return dateStr;
  }
}

export default function PulseActivityChart({ activityChart = [], windowDays = 7 }) {
  const [activeDate, setActiveDate] = useState(null);

  const days = useMemo(
    () => [...activityChart].sort((a, b) => a.date.localeCompare(b.date)),
    [activityChart],
  );

  const maxTotal = useMemo(
    () => Math.max(...days.map((d) => d.total || 0), 1),
    [days],
  );

  const hasActivity = useMemo(
    () => days.some((d) => (d.total || 0) > 0),
    [days],
  );

  const activeDay = useMemo(
    () => days.find((d) => d.date === activeDate)
      || [...days].reverse().find((d) => d.total > 0)
      || days[days.length - 1]
      || null,
    [days, activeDate],
  );

  const windowLabel = windowDays > 14
    ? 'by week'
    : `last ${windowDays} days`;

  if (!days.length) {
    return (
      <div className="nexus-pulse-activity nexus-pulse-activity--empty">
        <span className="nexus-pulse-activity-title">Activity</span>
        <p className="nexus-pulse-activity-empty-msg">No data for this filter in the chart window.</p>
      </div>
    );
  }

  if (!hasActivity) {
    return (
      <div className="nexus-pulse-activity nexus-pulse-activity--empty">
        <span className="nexus-pulse-activity-title">Activity ({windowLabel})</span>
        <p className="nexus-pulse-activity-empty-msg">
          No events in this period for the selected source. Try a longer time range (e.g. All time).
        </p>
      </div>
    );
  }

  return (
    <div className="nexus-pulse-activity">
      <div className="nexus-pulse-activity-head">
        <span className="nexus-pulse-activity-title">
          Activity (
          {windowLabel}
          )
        </span>
        {activeDay ? (
          <span className="nexus-pulse-activity-hint">
            {activeDay.bucket === 'week' ? weekLabel(activeDay.date) : fullDayLabel(activeDay.date)}
            {' · '}
            {(activeDay.total || 0).toLocaleString()}
            {' events'}
          </span>
        ) : null}
      </div>

      <div className="nexus-pulse-spark" role="list" aria-label="Daily event volume">
        {days.map((day, index) => {
          const pct = Math.round(((day.total || 0) / maxTotal) * 100);
          const isActive = activeDay?.date === day.date;
          const isLatest = index === days.length - 1;
          return (
            <button
              key={day.date}
              type="button"
              role="listitem"
              className={[
                'nexus-pulse-spark-col',
                isActive ? 'is-active' : '',
                isLatest ? 'is-latest' : '',
              ].filter(Boolean).join(' ')}
              onMouseEnter={() => setActiveDate(day.date)}
              onFocus={() => setActiveDate(day.date)}
              aria-label={`${fullDayLabel(day.date)}, ${day.total || 0} events`}
            >
              <div className="nexus-pulse-spark-track">
                <div
                  className="nexus-pulse-spark-bar"
                  style={{ '--bar-h': `${Math.max(pct, 6)}%`, '--bar-i': index }}
                />
              </div>
              <span className="nexus-pulse-spark-day">
                {day.bucket === 'week' ? weekLabel(day.date) : shortDayLabel(day.date)}
              </span>
              <span className="nexus-pulse-spark-val">{(day.total || 0).toLocaleString()}</span>
            </button>
          );
        })}
      </div>

      {activeDay ? (
        <div className="nexus-pulse-breakdown">
          {SOURCE_ORDER.map((code) => {
            const n = activeDay.bySource?.[code] || 0;
            if (!n) return null;
            const cfg = PULSE_SOURCE_CONFIG[code];
            const share = activeDay.total ? Math.round((n / activeDay.total) * 100) : 0;
            return (
              <div key={code} className="nexus-pulse-breakdown-row">
                <span className="nexus-pulse-breakdown-label">
                  <span className="nexus-pulse-breakdown-dot" style={{ background: cfg.color }} />
                  {cfg.label}
                </span>
                <span className="nexus-pulse-breakdown-bar-wrap">
                  <span
                    className="nexus-pulse-breakdown-bar"
                    style={{ width: `${share}%`, background: cfg.color }}
                  />
                </span>
                <span className="nexus-pulse-breakdown-n">{n.toLocaleString()}</span>
              </div>
            );
          })}
        </div>
      ) : null}
    </div>
  );
}
